/**
 * OpsAgent Service — wraps /api/admin/ai/ops-agent/ endpoints
 * Phase 4.8-B — WQ-200
 */

// === Types ===

export interface Anomaly {
  id: string;
  tenant_id: string;
  source: string;
  detection_strategy: string;
  severity: string;
  service_name: string;
  metric_name: string;
  metric_value: number;
  baseline_value: number;
  deviation: number;
  description: string | null;
  incident_id: string | null;
  is_resolved: boolean;
  resolved_at: string | null;
  detected_at: string;
  created_at: string;
}

export interface Incident {
  id: string;
  tenant_id: string;
  title: string;
  severity: string;
  anomaly_ids: string[];
  services_affected: string[];
  probable_cause: string | null;
  status: string;
  detected_at: string;
  resolved_at: string | null;
  created_at: string;
}

export interface Dependency {
  id: string;
  tenant_id: string;
  component: string;
  category: string;
  current_version: string;
  latest_version: string | null;
  eol_date: string | null;
  status: string;
  checked_at: string;
  source_url: string | null;
}

export interface Probe {
  id: string;
  name: string;
  description: string | null;
  linked_audit: string | null;
  schedule_interval: string | null;
  last_status: string | null;
  last_run_at: string | null;
  success_rate_7d: number | null;
  is_active: boolean;
}

export interface Baseline {
  id: string;
  service_name: string;
  metric_name: string;
  baseline_mean: number;
  baseline_stddev: number;
  baseline_p95: number;
  sample_count: number;
  window_hours: number;
  computed_at: string;
}

export interface OpsAgentSummary {
  anomalies: { total: number; by_severity: Record<string, number>; unresolved: number };
  open_incidents: number;
  dependencies: { total: number; outdated: number; eol: number };
  probes: { total: number; active: number; failing: number };
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function getSummary(authFetch: FetchFn): Promise<OpsAgentSummary> {
  const res = await authFetch('/api/admin/ai/ops-agent/summary');
  if (!res.ok) throw new Error(`Failed to get OpsAgent summary: ${res.status}`);
  return res.json();
}

export async function listAnomalies(
  authFetch: FetchFn,
  params?: { severity?: string; service?: string; source?: string; is_resolved?: boolean; limit?: number },
): Promise<Anomaly[]> {
  const qs = new URLSearchParams();
  if (params?.severity) qs.set('severity', params.severity);
  if (params?.service) qs.set('service', params.service);
  if (params?.source) qs.set('source', params.source);
  if (params?.is_resolved !== undefined) qs.set('is_resolved', String(params.is_resolved));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  const res = await authFetch(`/api/admin/ai/ops-agent/anomalies${query ? '?' + query : ''}`);
  if (!res.ok) throw new Error(`Failed to list anomalies: ${res.status}`);
  return res.json();
}

export async function getAnomaly(authFetch: FetchFn, anomalyId: string): Promise<Anomaly> {
  const res = await authFetch(`/api/admin/ai/ops-agent/anomalies/${anomalyId}`);
  if (!res.ok) throw new Error(`Failed to get anomaly: ${res.status}`);
  return res.json();
}

export async function resolveAnomaly(authFetch: FetchFn, anomalyId: string): Promise<Anomaly> {
  const res = await authFetch(`/api/admin/ai/ops-agent/anomalies/${anomalyId}/resolve`, {
    method: 'PATCH',
  });
  if (!res.ok) throw new Error(`Failed to resolve anomaly: ${res.status}`);
  return res.json();
}

export async function listIncidents(
  authFetch: FetchFn,
  params?: { severity?: string; status?: string; limit?: number },
): Promise<Incident[]> {
  const qs = new URLSearchParams();
  if (params?.severity) qs.set('severity', params.severity);
  if (params?.status) qs.set('status', params.status);
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  const res = await authFetch(`/api/admin/ai/ops-agent/incidents${query ? '?' + query : ''}`);
  if (!res.ok) throw new Error(`Failed to list incidents: ${res.status}`);
  return res.json();
}

export async function getIncident(authFetch: FetchFn, incidentId: string): Promise<Incident> {
  const res = await authFetch(`/api/admin/ai/ops-agent/incidents/${incidentId}`);
  if (!res.ok) throw new Error(`Failed to get incident: ${res.status}`);
  return res.json();
}

export async function acknowledgeIncident(authFetch: FetchFn, incidentId: string): Promise<Incident> {
  const res = await authFetch(`/api/admin/ai/ops-agent/incidents/${incidentId}/acknowledge`, {
    method: 'PATCH',
  });
  if (!res.ok) throw new Error(`Failed to acknowledge incident: ${res.status}`);
  return res.json();
}

export async function resolveIncident(authFetch: FetchFn, incidentId: string): Promise<Incident> {
  const res = await authFetch(`/api/admin/ai/ops-agent/incidents/${incidentId}/resolve`, {
    method: 'PATCH',
  });
  if (!res.ok) throw new Error(`Failed to resolve incident: ${res.status}`);
  return res.json();
}

export async function listDependencies(authFetch: FetchFn): Promise<Dependency[]> {
  const res = await authFetch('/api/admin/ai/ops-agent/dependencies');
  if (!res.ok) throw new Error(`Failed to list dependencies: ${res.status}`);
  return res.json();
}

export async function listProbes(authFetch: FetchFn): Promise<Probe[]> {
  const res = await authFetch('/api/admin/ai/ops-agent/probes');
  if (!res.ok) throw new Error(`Failed to list probes: ${res.status}`);
  return res.json();
}

export async function runProbe(authFetch: FetchFn, probeId: string): Promise<Probe> {
  const res = await authFetch(`/api/admin/ai/ops-agent/probes/${probeId}/run`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to run probe: ${res.status}`);
  return res.json();
}

export async function listBaselines(authFetch: FetchFn): Promise<Baseline[]> {
  const res = await authFetch('/api/admin/ai/ops-agent/baselines');
  if (!res.ok) throw new Error(`Failed to list baselines: ${res.status}`);
  return res.json();
}
